import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { LoadingSpinner, LoginPage } from '@elka/ui';
import { useAuth, hasStoredWorkerSession } from '@/features/auth/hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading, signOut } = useAuth();
  // Track which uid has been verified and its result — avoids synchronous setState in effect body
  const [verifiedClaims, setVerifiedClaims] = useState<{ uid: string; isWorker: boolean } | null>(
    null
  );

  const userUid = user?.uid;

  useEffect(() => {
    if (isLoading || !user || !userUid) return;

    // Check custom claims to ensure this user has been verified as a known worker
    user
      .getIdTokenResult()
      .then((tokenResult) => {
        setVerifiedClaims({ uid: userUid, isWorker: tokenResult.claims.workerRole === 'worker' });
      })
      .catch(() => {
        // Offline with an expired token: trust the session verified online on this device.
        setVerifiedClaims({ uid: userUid, isWorker: hasStoredWorkerSession() });
      });
  }, [userUid, isLoading, user]);

  // Derive states without any synchronous setState in the effect body
  const isVerifying = !isLoading && !!user && verifiedClaims?.uid !== userUid;
  const isVerifiedWorker =
    verifiedClaims !== null && verifiedClaims.uid === userUid ? verifiedClaims.isWorker : false;

  // `isLoading` from useAuth now stays true until the tenant/claims resolution for the
  // current auth state completes, so rendering here can't flash legacy ('_' path) data
  // before the session tenant is pinned.
  if (isLoading || isVerifying) {
    return (
      <div className="bg-page flex min-h-screen flex-col items-center justify-center gap-3">
        <LoadingSpinner size="lg" centered={false} />
        <p className="text-muted text-sm">טוען...</p>
      </div>
    );
  }

  // User is Firebase-authenticated but not a registered worker
  if (user && !isVerifiedWorker) {
    return (
      <LoginPage
        calm
        title="אין גישה"
        subtitle="מספר הטלפון שלך לא רשום במערכת. פנה למנהל כדי להוסיף אותך."
        error={null}
        brandTitle="ELKA · פורטל צוות"
        valueProps={['דיווח שעות, סידור עבודה', 'ותלושי שכר —', 'הכל במקום אחד.']}
      >
        <button
          onClick={() => signOut()}
          className="rounded-control border-line-strong text-ink-2 hover:bg-surface-soft hover:border-brand mt-6 flex min-h-[52px] w-full items-center justify-center gap-3 border bg-white py-3.5 text-base font-bold transition-colors"
        >
          חזרה למסך הכניסה
        </button>
      </LoginPage>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
